import type { FieldType } from '@kizen/api';
import type { ActivityRulePayload, VisibilityRule } from './types';
import type { ConditionValue } from './activity-rules-metadata';
import { isAfter, isBefore, isWithinInterval, isValid, parse } from 'date-fns';

type EvalMap = ReturnType<typeof getRuleEvalMap>;

type DateCondition = keyof typeof dateOps | 'is_blank';

type EvalRulesContext = {
  currentUserId: string;
  fieldTypes: Record<string, FieldType>;
  rules: EvalMap;
  values: Record<string, null | boolean | number | string | string[]>;
};

export type LogActivityFieldValue =
  | {
      customObjectFieldId?: string;
      fieldType: 'checkbox';
      id: string;
      value?: boolean;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'checkboxes';
      id: string;
      value?: string[];
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'date';
      id: string;
      value?: string;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'datetime';
      id: string;
      value?: string;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'decimal';
      id: string;
      value: string | null;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'dropdown';
      id: string;
      value?: string;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'dynamictags';
      id: string;
      value?: { id: string }[];
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'email';
      id: string;
      value: string;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'files';
      id: string;
      value:
        | null
        | {
            id: string;
            created: string;
            key: string;
            name: string;
            url: string;
            thumbnail_url: string;
            size_bytes: number;
            size_formatted: string;
            content_type: string;
            is_public: boolean;
            common_key: null | string;
            is_common: boolean;
            metadata: Record<string, any>;
            employee: {
              id: string;
              email: string;
              first_name: string;
              last_name: string;
              display_name: string;
            };
          }[];
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'integer';
      id: string;
      value: string | null;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'longtext';
      id: string;
      value: string | null;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'money';
      id: string;
      value: string | null;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'phonenumber';
      id: string;
      value: string | { e164: string } | null;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'radio';
      id: string;
      value: string | null;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'rating';
      id: string;
      value: string | null;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'relationship';
      id: string;
      value?: { id: string } | { id: string }[];
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'status';
      id: string;
      value?: string;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'team_selector';
      id: string;
      value: null | {
        displayName: string;
        email: string;
        firstName: string;
        id: string;
        lastName: string;
      };
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'text';
      id: string;
      value: string;
    }
  | {
      customObjectFieldId?: string;
      fieldType: 'yesnomaybe';
      id: string;
      value: string | null;
    };

type TestFn = (self: string, ctx: EvalRulesContext) => boolean;

const ISORegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

const hasAny = (a: any, b: any) => {
  if (a === null) {
    return false;
  }

  if (Array.isArray(a)) {
    return b.some((x: any) => a.includes(x));
  }

  return b.includes(a);
};
const notHasAny = (a: any, b: any) => {
  return !hasAny(a, b);
};
const isBlank = (a: any) => {
  return Array.isArray(a) ? a.length === 0 : !a;
};
const isNotBlank = (a: any) => {
  return Array.isArray(a) ? a.length > 0 : Boolean(a);
};
const eq = (a: any, b: any) => {
  return a === b;
};
const notEq = (a: any, b: any) => {
  return a !== b;
};
const lessThan = (a: any, b: any) => {
  return a === null ? false : a < b;
};
const lessThanOrEqual = (a: any, b: any) => {
  return a === null ? false : a <= b;
};
const greaterThan = (a: any, b: any) => {
  return a === null ? false : a > b;
};
const greaterThanOrEqual = (a: any, b: any) => {
  return a === null ? false : a >= b;
};
const contains = (a: string, b: string) => {
  return a ? a.includes(b) : false;
};
const notContains = (a: string, b: string) => {
  return a ? !a.includes(b) : true;
};
const startsWith = (a: string, b: string) => {
  return a ? a.startsWith(b) : false;
};
const notStartsWith = (a: string, b: string) => {
  return a ? !a.startsWith(b) : true;
};
const endsWith = (a: string, b: string) => {
  return a ? a.endsWith(b) : false;
};
const notEndsWith = (a: string, b: string) => {
  return a ? !a.endsWith(b) : true;
};
const between = (x: any, range: any) => {
  if (!x || !range || !Array.isArray(range)) {
    return false;
  }

  return x >= range[0] && x <= range[1];
};
const earlierThan = (a: any, b: any): boolean => {
  if (!a || !b) {
    return false;
  }

  const d1 = ISORegex.test(a) ? new Date(a) : parse(a, 'yyyy-M-d', new Date());
  const d2 = ISORegex.test(b) ? new Date(b) : parse(b, 'yyyy-M-d', new Date());

  if (!isValid(d1) || !isValid(d2)) {
    return false;
  }

  return isBefore(d1, d2);
};
const earlierThanOrOn = (a: any, b: any): boolean => {
  return earlierThan(a, b) || eq(a, b);
};
const laterThan = (a: any, b: any): boolean => {
  if (!a || !b) {
    return false;
  }

  const d1 = ISORegex.test(a) ? new Date(a) : parse(a, 'yyyy-M-d', new Date());
  const d2 = ISORegex.test(b) ? new Date(b) : parse(b, 'yyyy-M-d', new Date());

  if (!isValid(d1) || !isValid(d2)) {
    return false;
  }

  return isAfter(d1, d2);
};
const laterThanOrOn = (a: any, b: any): boolean => {
  return laterThan(a, b) || eq(a, b);
};
const dateBetween = (date: any, range: any): boolean => {
  if (!date || !range || !Array.isArray(range)) {
    return false;
  }

  const start = parse(range[0], 'yyyy-M-d', new Date());
  const end = parse(range[1], 'yyyy-M-d', new Date());
  const d = ISORegex.test(date)
    ? new Date(date)
    : parse(date, 'yyyy-M-d', new Date());

  if (!isValid(start) || !isValid(end) || !isValid(d)) {
    return false;
  }

  return isWithinInterval(d, { start, end });
};
const monthEq = (a: any, b: any) => {
  if (!a) {
    return false;
  }

  const date = ISORegex.test(a)
    ? new Date(a)
    : parse(a, 'yyyy-M-d', new Date());

  if (!isValid(date)) {
    return false;
  }

  const a_month = date.getMonth() + 1;
  const b_month = parseInt(b);
  return a_month === b_month;
};

const ops: Record<ConditionValue, any> = {
  '=': eq,
  '!=': notEq,
  has_any: hasAny,
  '!has_any': notHasAny,
  is_any_of: hasAny,
  is_none_of: notHasAny,
  is_blank: isBlank,
  between,
  '<': lessThan,
  '<=': lessThanOrEqual,
  '>': greaterThan,
  '>=': greaterThanOrEqual,
  month_equals: monthEq,
  contains,
  '!contains': notContains,
  starts_with: startsWith,
  '!starts_with': notStartsWith,
  ends_with: endsWith,
  '!ends_with': notEndsWith,
};

const dateOps = {
  '=': eq,
  '!=': notEq,
  '<': earlierThan,
  '<=': earlierThanOrOn,
  '>': laterThan,
  '>=': laterThanOrOn,
  between: dateBetween,
  month_equals: monthEq,
};

const isDateOrDateTime = (
  ft: string,
  condition?: ConditionValue
): condition is DateCondition => {
  return ft === 'date' || ft === 'datetime';
};

const parseNumbers = (
  value: ActivityRulePayload['value'],
  parser: typeof parseInt | typeof parseFloat
) => {
  if (Array.isArray(value)) {
    return value.map((x) => parser(x));
  }

  if (typeof value === 'string') {
    return parser(value);
  }

  return value;
};

export const getRuleEvalMap = (visibilityRules?: VisibilityRule[]) => {
  if (!visibilityRules) return new Map<string, TestFn>();

  return visibilityRules.reduce<Map<string, TestFn>>(
    (acc, { layout_items, rule }) => {
      const test = (self: string, ctx: EvalRulesContext) => {
        const { currentUserId, rules, values } = ctx;
        const hasValues = Object.keys(values).length > 0;

        const conditions = rule.filters.map((payload) => {
          const { activity_field_id, condition, value } = payload;

          if (activity_field_id === self) {
            // We don't allow configuring rules for fields to "hide themselves" but we
            // need to implement the logic to prevent infinite recursion.
            return false;
          }

          const isConditionFieldHiddenByRule =
            rules.has(activity_field_id) &&
            !rules.get(activity_field_id)!(activity_field_id, ctx);

          const isSourceFieldDeleted =
            hasValues && !(activity_field_id in values);

          const src =
            isConditionFieldHiddenByRule || isSourceFieldDeleted
              ? undefined
              : values[activity_field_id];

          if (condition === '=' && value === 'is_me') {
            return src === currentUserId;
          }

          if (condition === 'is_blank') {
            return value ? isBlank(src) : isNotBlank(src);
          }

          const field_type = ctx.fieldTypes[activity_field_id];

          if (isDateOrDateTime(field_type, condition)) {
            return dateOps[condition](src, value);
          }

          if (field_type === 'integer' || field_type === 'rating') {
            const a = typeof src === 'string' ? parseInt(src) : src;
            const b = parseNumbers(value, parseInt);
            return ops[condition](a, b);
          }

          if (field_type === 'decimal' || field_type === 'money') {
            const a = typeof src === 'string' ? parseFloat(src) : src;
            const b = parseNumbers(value, parseFloat);
            return ops[condition](a, b);
          }

          if (!ops[condition]) {
            return false;
          }

          return ops[condition](src, value);
        });

        const activeConditions = conditions.filter((x) => x !== null);

        return rule.and
          ? activeConditions.every(Boolean)
          : activeConditions.some(Boolean);
      };

      for (const item of layout_items) {
        if ('field_id' in item) {
          acc.set(item.field_id, test);
        } else {
          acc.set(item.internal_name, test);
        }
      }

      return acc;
    },
    new Map()
  );
};

/**
 * @example ```
 * convertDateString('2025-08-05') // '2025-8-5'
 * ```
 */
const removeLeadingZerosFromDate = (date?: string) => {
  if (!date) {
    return null;
  }

  const parts = date.split('-');
  const mapped = parts.map((part, i) => {
    if (i > 0 && part.startsWith('0')) {
      return part.slice(1);
    }
    return part;
  });

  return mapped.join('-');
};

/**
 * Get field values key by the field id. The fields parameter is the log activity form state.
 */
export const getFieldValues = (
  fields?: LogActivityFieldValue[]
): EvalRulesContext['values'] => {
  if (!fields) return {};
  return fields.reduce<EvalRulesContext['values']>(
    (acc, { id, fieldType, value }) => {
      if (fieldType === 'relationship') {
        acc[id] = Array.isArray(value)
          ? value.map((x) => x.id)
          : value
            ? [value.id]
            : [];
      } else if (fieldType === 'dynamictags') {
        acc[id] = value?.map((x) => x.id) ?? [];
      } else if (fieldType === 'team_selector') {
        acc[id] = value?.id ?? null;
      } else if (fieldType === 'date') {
        acc[id] = removeLeadingZerosFromDate(value);
      } else if (fieldType === 'files') {
        acc[id] = value?.length ?? 0;
      } else if (fieldType === 'checkbox') {
        acc[id] = Boolean(value);
      } else if (fieldType === 'phonenumber') {
        acc[id] = value && typeof value === 'object' ? value.e164 : value;
      } else {
        acc[id] = value ?? null;
      }

      return acc;
    },
    {}
  );
};

export const evalRules = (ctx: EvalRulesContext) => {
  return [...ctx.rules.entries()].reduce<Record<string, boolean>>(
    (acc, [id, testFn]) => {
      acc[id] = testFn(id, ctx);
      return acc;
    },
    {}
  );
};
